/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - FilterPills Component
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Pills para filtrar infraestructuras por nivel de score.
 * Incluye contadores, colores según nivel y botón de cierre.
 *
 * Uso:
 *   <FilterPills
 *     counts={{ all: 12, high: 3, medium: 5, low: 2, critical: 2 }}
 *     selected="all"
 *     onSelect={(filter) => setFilter(filter)}
 *   />
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import { colors, typography, borderRadius, spacing, transitions } from '../design-tokens';
import type { ScoreClass } from '../design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export type FilterOption = 'all' | ScoreClass;

export interface FilterCounts {
  all: number;
  high: number;
  medium: number;
  low: number;
  critical: number;
}

export interface FilterPillsProps {
  /** Contadores para cada filtro */
  counts: FilterCounts;
  /** Filtro actualmente seleccionado */
  selected: FilterOption;
  /** Callback cuando se selecciona un filtro */
  onSelect: (filter: FilterOption) => void;
  /** Clases CSS adicionales */
  className?: string;
  /** ID para testing */
  testId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN
// ─────────────────────────────────────────────────────────────────────────────

const filterConfig: Record<FilterOption, { label: string; color: string }> = {
  all: { label: 'Todos', color: colors.primary },
  high: { label: '≥90', color: colors.scores.high },
  medium: { label: '70-89', color: colors.scores.medium },
  low: { label: '50-69', color: colors.scores.low },
  critical: { label: '<50', color: colors.scores.critical },
};

const filterOrder: FilterOption[] = ['all', 'high', 'medium', 'low', 'critical'];

// ─────────────────────────────────────────────────────────────────────────────
// ICONO X (CERRAR)
// ─────────────────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 10, height: 10 }}
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PILL INDIVIDUAL
// ─────────────────────────────────────────────────────────────────────────────

interface PillProps {
  filter: FilterOption;
  count: number;
  isActive: boolean;
  onClick: () => void;
  onClose: () => void;
}

function Pill({ filter, count, isActive, onClick, onClose }: PillProps) {
  const config = filterConfig[filter];
  const isAll = filter === 'all';

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`filter-pill-${filter}`}
      data-active={isActive}
      data-score={isAll ? undefined : filter}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing[2],
        padding: `${spacing[2]} ${spacing[4]}`,
        background: isActive ? `${config.color}26` : colors.bg.secondary,
        border: `1px solid ${isActive ? config.color : colors.border.DEFAULT}`,
        borderRadius: borderRadius.full,
        fontFamily: typography.fontFamily.sans,
        fontSize: typography.fontSize.sm,
        color: isActive ? config.color : colors.fg.secondary,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = config.color;
          e.currentTarget.style.color = config.color;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.borderColor = colors.border.DEFAULT;
          e.currentTarget.style.color = colors.fg.secondary;
        }
      }}
    >
      <span>{config.label}</span>
      <span
        data-testid={`filter-count-${filter}`}
        style={{
          fontFamily: typography.fontFamily.mono,
          fontSize: typography.fontSize['2xs'],
          padding: '2px 5px',
          borderRadius: borderRadius.sm,
          backgroundColor: isActive ? config.color : colors.bg.tertiary,
          color: isActive ? colors.bg.primary : 'inherit',
        }}
      >
        {count}
      </span>
      {/* Botón X - solo visible cuando está activo */}
      {isActive && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`filter-close-${filter}`}
          onClick={handleClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 14,
            height: 14,
            padding: 0,
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            opacity: 0.6,
            transition: `opacity ${transitions.fast}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
        >
          <CloseIcon />
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export function FilterPills({
  counts,
  selected,
  onSelect,
  className = '',
  testId,
}: FilterPillsProps) {
  
  // Al cerrar un pill, volver a "all"
  const handleClose = () => {
    onSelect('all');
  };

  return (
    <div
      role="group"
      aria-label="Filtrar por puntuación"
      data-testid={testId ?? 'filter-pills'}
      className={className}
      style={{
        display: 'flex',
        gap: spacing[2],
        flexWrap: 'wrap',
      }}
    >
      {filterOrder.map((filter) => (
        <Pill
          key={filter}
          filter={filter}
          count={counts[filter]}
          isActive={selected === filter}
          onClick={() => onSelect(filter)}
          onClose={handleClose}
        />
      ))}
    </div>
  );
}

export default FilterPills;
